import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Plus,
  ArrowUpRight,
  ListChecks,
  RefreshCw,
  Upload,
  X,
  Tag,
  BadgeCheck,
  XCircle,
  Hourglass,
} from 'lucide-react';
import {
  Card,
  CardContent,
  Button,
  Input,
  Select,
  Badge,
  EmptyState,
  Pagination,
  Checkbox,
} from '../components/ui';
import { blogsApi } from '../lib/api';
import { cn, formatRelativeTime } from '../lib/utils';
import { getStatusConfig, getErrorMessage } from '../lib/statusConfig';
import type { BlogJobStatusResponse, ApprovalStatus } from '../types';
import toast from 'react-hot-toast';

const statusFilterOptions = [
  { value: 'all',        label: 'All Status'  },
  { value: 'pending',    label: 'Pending'     },
  { value: 'processing', label: 'Processing'  },
  { value: 'completed',  label: 'Completed'   },
  { value: 'failed',     label: 'Failed'      },
];

// ─── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  bgColor: string;
  active?: boolean;
  onClick?: () => void;
}> = ({ label, value, icon, bgColor, active, onClick }) => (
  <Card
    className={cn(
      'cursor-default transition-all',
      onClick && 'cursor-pointer hover:shadow-md',
      active && 'ring-2 ring-primary-500 ring-offset-2 dark:ring-offset-gray-900'
    )}
    onClick={onClick}
  >
    <CardContent className="p-4 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${bgColor}`}>{icon}</div>
      <div>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      </div>
    </CardContent>
  </Card>
);

// ─── Approval Config ───────────────────────────────────────────────────────────
const approvalConfig: Record<ApprovalStatus, {
  label: string;
  icon: React.ReactNode;
  className: string;
}> = {
  pending_review: {
    label: 'Pending Review',
    icon: <Hourglass className="h-3 w-3" />,
    className: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800',
  },
  approved: {
    label: 'Approved',
    icon: <BadgeCheck className="h-3 w-3" />,
    className: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800',
  },
  rejected: {
    label: 'Rejected',
    icon: <XCircle className="h-3 w-3" />,
    className: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800',
  },
};

// ─── Job Row ───────────────────────────────────────────────────────────────────
const JobRow: React.FC<{
  job: BlogJobStatusResponse;
  index: number;
  isSelected: boolean;
  onToggle: () => void;
}> = ({ job, index, isSelected, onToggle }) => {
  const cfg          = getStatusConfig(job.status);
  const isActive     = job.status === 'pending' || job.status === 'processing';
  // Checkbox enabled only when completed AND not already approved
  const isSelectable = job.status === 'completed' && job.approval_status !== 'approved';
  const approvalCfg  = approvalConfig[job.approval_status] ?? approvalConfig['pending_review'];

  return (
    <motion.tr
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className={cn(
        'transition-colors group',
        isSelected
          ? 'bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-50 dark:hover:bg-primary-900/25'
          : job.approval_status === 'approved'
          ? 'bg-emerald-50/30 dark:bg-emerald-900/10 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20'
          : isActive
          ? 'bg-blue-50/40 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
      )}
      onClick={() => isSelectable && onToggle()}
    >
      {/* Checkbox */}
      <td className="pl-5 pr-2 py-4 w-10" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          disabled={!isSelectable}
          onChange={onToggle}
        />
      </td>

      {/* Status */}
      <td className="px-4 py-4 whitespace-nowrap">
        <Badge variant={cfg.variant as any} icon={cfg.icon}>
          {cfg.label}
        </Badge>
      </td>

      {/* Title + keywords + error */}
      <td className="px-4 py-4">
        <Link
          to={`/blogs/${job.job_id}`}
          onClick={(e) => e.stopPropagation()}
          className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors line-clamp-1 max-w-xs lg:max-w-md block"
        >
          {job.selected_title || 'Untitled'}
        </Link>
        {job.keywords && job.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {job.keywords.slice(0, 3).map((kw) => (
              <span
                key={kw}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
              >
                <Tag className="h-2.5 w-2.5" />
                {kw}
              </span>
            ))}
            {job.keywords.length > 3 && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500 self-center">
                +{job.keywords.length - 3}
              </span>
            )}
          </div>
        )}
        {job.error && (
          <p className="text-xs text-red-500 mt-1 truncate max-w-xs">
            {getErrorMessage(job.error, job.error_code)}
          </p>
        )}
      </td>

      {/* Approval status — only shown for completed jobs */}
      <td className="px-4 py-4 whitespace-nowrap hidden md:table-cell">
        {job.status === 'completed' ? (
          <span className={cn(
            'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium',
            approvalCfg.className
          )}>
            {approvalCfg.icon}
            {approvalCfg.label}
          </span>
        ) : (
          <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
        )}
      </td>

      {/* Created */}
      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden lg:table-cell">
        {formatRelativeTime(job.created_at)}
      </td>

      {/* Word Count */}
      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden xl:table-cell">
        {job.result?.word_count
          ? `${job.result.word_count.toLocaleString()} words`
          : '—'}
      </td>

      {/* Actions */}
      <td className="px-4 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
        <Link to={`/blogs/${job.job_id}`}>
          <Button variant="ghost" size="sm" rightIcon={<ArrowUpRight className="h-3.5 w-3.5" />}>
            View
          </Button>
        </Link>
      </td>
    </motion.tr>
  );
};

// ─── Table Skeleton ────────────────────────────────────────────────────────────
const TableSkeleton: React.FC = () => (
  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
    {Array.from({ length: 8 }).map((_, i) => (
      <tr key={i} className="animate-pulse">
        <td className="pl-5 pr-2 py-4 w-10">
          <div className="h-4 w-4 skeleton rounded" />
        </td>
        <td className="px-4 py-4">
          <div className="h-5 w-20 skeleton rounded-full" />
        </td>
        <td className="px-4 py-4">
          <div className="h-5 w-48 skeleton rounded mb-1.5" />
          <div className="flex gap-1">
            <div className="h-4 w-12 skeleton rounded" />
            <div className="h-4 w-14 skeleton rounded" />
          </div>
        </td>
        <td className="px-4 py-4 hidden md:table-cell">
          <div className="h-5 w-24 skeleton rounded-full" />
        </td>
        <td className="px-4 py-4 hidden lg:table-cell">
          <div className="h-4 w-20 skeleton rounded" />
        </td>
        <td className="px-4 py-4 hidden xl:table-cell">
          <div className="h-4 w-16 skeleton rounded" />
        </td>
        <td className="px-4 py-4 text-right">
          <div className="h-8 w-16 skeleton rounded ml-auto" />
        </td>
      </tr>
    ))}
  </tbody>
);

const PAGE_SIZE = 10;

// ─── Main Component ────────────────────────────────────────────────────────────
export const JobQueueMonitor: React.FC = () => {
  const navigate      = useNavigate();
  const queryClient   = useQueryClient();
  const [searchQuery, setSearchQuery]   = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds]   = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage]   = useState(1);

  const { data, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['jobs', 'monitor', currentPage, statusFilter, searchQuery],
    queryFn: () => blogsApi.getJobs(currentPage, PAGE_SIZE),
    staleTime: 0,
    refetchOnMount: 'always' as const,
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      const hasActive = items.some(
        (j) => j.status === 'pending' || j.status === 'processing' || j.status.startsWith('retrying')
      );
      return hasActive ? 5000 : 30000;
    },
  });

  const jobs       = data?.items ?? [];
  const totalPages = data?.total_pages ?? 1;
  const total      = data?.total ?? 0;

  // Stats counted from current page — server provides totals separately via stat cards
  const stats = {
    total:      total,
    pending:    jobs.filter((j) => j.status === 'pending').length,
    processing: jobs.filter((j) => j.status === 'processing').length,
    completed:  jobs.filter((j) => j.status === 'completed').length,
    failed:     jobs.filter((j) => j.status === 'failed').length,
    approved:   jobs.filter((j) => j.approval_status === 'approved').length,
  };

  const hasActiveJobs = stats.pending > 0 || stats.processing > 0;

  // Client-side title search filter within current page
  const filteredJobs = jobs.filter((job) => {
    const matchesSearch =
      searchQuery === '' ||
      (job.selected_title || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Reset to page 1 when status filter changes
  const handleStatusFilter = (val: string) => {
    setStatusFilter(val);
    setCurrentPage(1);
  };

  // Selectable on current page: completed + not yet approved
  const selectableJobs = filteredJobs.filter(
    (j) => j.status === 'completed' && j.approval_status !== 'approved'
  );
  const allSelected    = selectableJobs.length > 0 && selectableJobs.every((j) => selectedIds.has(j.job_id));
  const someSelected   = selectableJobs.some((j) => selectedIds.has(j.job_id));
  const isIndeterminate = someSelected && !allSelected;

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        selectableJobs.forEach((j) => next.delete(j.job_id));
      } else {
        selectableJobs.forEach((j) => next.add(j.job_id));
      }
      return next;
    });
  };

  const toggleJob = (jobId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const approveMutation = useMutation({
    mutationFn: (job_ids: number[]) => blogsApi.approve({ job_ids }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      clearSelection();
      if (res.failed.length === 0) {
        toast.success(`${res.approved.length} blog${res.approved.length !== 1 ? 's' : ''} approved & published to portal`);
      } else {
        toast.success(`${res.approved.length} approved`);
        toast.error(
          `${res.failed.length} failed: ${res.failed.map((f) => `#${f.job_id} — ${f.reason}`).join(', ')}`,
          { duration: 6000 }
        );
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Approval failed');
    },
  });

  const handleApprove = () => {
    approveMutation.mutate(Array.from(selectedIds));
  };

  return (
    <div className="space-y-6 animate-fade-in pb-28">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Job Queue Monitor</h1>
          <p className="text-gray-500 dark:text-gray-400">
            Track, review and approve blog generation jobs
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasActiveJobs && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm font-medium">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
              </span>
              Live
            </div>
          )}
          <Link to="/generate">
            <Button leftIcon={<Plus className="h-4 w-4" />}>New Job</Button>
          </Link>
        </div>
      </div>

      {/* ── Stats Grid ── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Total Jobs"
          value={stats.total}
          icon={<ListChecks className="h-6 w-6 text-primary-600" />}
          bgColor="bg-primary-100 dark:bg-primary-900/30"
          active={statusFilter === 'all'}
          onClick={() => handleStatusFilter('all')}
        />
        <StatCard
          label="Pending"
          value={stats.pending}
          icon={<Clock className="h-6 w-6 text-yellow-600" />}
          bgColor="bg-yellow-100 dark:bg-yellow-900/30"
          active={statusFilter === 'pending'}
          onClick={() => handleStatusFilter('pending')}
        />
        <StatCard
          label="Processing"
          value={stats.processing}
          icon={<Loader2 className="h-6 w-6 text-blue-600 animate-spin" />}
          bgColor="bg-blue-100 dark:bg-blue-900/30"
          active={statusFilter === 'processing'}
          onClick={() => handleStatusFilter('processing')}
        />
        <StatCard
          label="Completed"
          value={stats.completed}
          icon={<CheckCircle className="h-6 w-6 text-green-600" />}
          bgColor="bg-green-100 dark:bg-green-900/30"
          active={statusFilter === 'completed'}
          onClick={() => handleStatusFilter('completed')}
        />
        <StatCard
          label="Approved"
          value={stats.approved}
          icon={<BadgeCheck className="h-6 w-6 text-emerald-600" />}
          bgColor="bg-emerald-100 dark:bg-emerald-900/30"
        />
      </div>

      {/* ── Failed Alert ── */}
      {stats.failed > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{stats.failed} job{stats.failed !== 1 ? 's' : ''} failed</span>
          <button
            onClick={() => handleStatusFilter('failed')}
            className="ml-auto text-red-600 dark:text-red-300 hover:underline text-xs font-medium"
          >
            View failed
          </button>
        </motion.div>
      )}

      {/* ── Filter Bar ── */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1">
              <Input
                placeholder="Search by job title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                leftIcon={<Search className="h-5 w-5" />}
              />
            </div>
            <div className="w-full md:w-48">
              <Select
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
                options={statusFilterOptions}
              />
            </div>
            {dataUpdatedAt > 0 && (
              <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap self-center">
                <RefreshCw className={`h-3.5 w-3.5 ${hasActiveJobs ? 'animate-spin-slow' : ''}`} />
                {formatRelativeTime(new Date(dataUpdatedAt).toISOString())}
              </div>
            )}
          </div>

          {/* Selection hint */}
          {selectableJobs.length > 0 && (
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
              {selectableJobs.length} completed blog{selectableJobs.length !== 1 ? 's' : ''} — click a row or checkbox to select, then approve
            </p>
          )}
        </CardContent>
      </Card>

      {/* ── Job Table ── */}
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                {/* Select-all checkbox */}
                <th className="pl-5 pr-2 py-3.5 w-10">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={isIndeterminate}
                    disabled={selectableJobs.length === 0}
                    onChange={toggleAll}
                  />
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Title / Keywords
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                  Approval
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  Created
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell">
                  Word Count
                </th>
                <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>

            {isLoading ? (
              <TableSkeleton />
            ) : (
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredJobs.map((job, index) => (
                  <JobRow
                    key={job.job_id}
                    job={job}
                    index={index}
                    isSelected={selectedIds.has(job.job_id)}
                    onToggle={() => toggleJob(job.job_id)}
                  />
                ))}
              </tbody>
            )}
          </table>
        </div>

        {/* Empty state */}
        {!isLoading && filteredJobs.length === 0 && (
          <div className="p-8">
            <EmptyState
              title="No jobs found"
              description={
                statusFilter !== 'all' || searchQuery
                  ? 'Try adjusting your search or filter criteria'
                  : 'Start by generating your first blog post'
              }
              icon={<ListChecks className="h-8 w-8" />}
              action={
                statusFilter === 'all' && !searchQuery
                  ? { label: 'Generate Blog', onClick: () => navigate('/generate') }
                  : undefined
              }
            />
          </div>
        )}
      </Card>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <div className="space-y-1">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            total={total}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
            isLoading={isLoading}
          />
          {selectedIds.size > 0 && (
            <p className="text-xs text-center text-primary-500 font-medium">
              {selectedIds.size} blog{selectedIds.size !== 1 ? 's' : ''} selected across pages
            </p>
          )}
        </div>
      )}

      {/* ── Floating Approve Bar ── */}
      <AnimatePresence>
        {selectedIds.size > 0 && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
          >
            <div className="flex items-center gap-3 px-5 py-3.5 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 backdrop-blur-sm">
              {/* Count pill */}
              <div className="flex items-center gap-2 pr-3 border-r border-gray-200 dark:border-gray-600">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold">
                  {selectedIds.size}
                </span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  blog{selectedIds.size !== 1 ? 's' : ''} selected
                </span>
              </div>

              {/* Approve button */}
              <Button
                size="sm"
                onClick={handleApprove}
                isLoading={approveMutation.isPending}
                leftIcon={<Upload className="h-4 w-4" />}
                className="whitespace-nowrap"
              >
                Approve & Publish
              </Button>

              {/* Clear */}
              <button
                onClick={clearSelection}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Clear selection"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
